import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import { Star, Cake, Heart, Umbrella, Camera } from "lucide-react";

const MOMENTS = [
  { icon: Cake, title: "Family Celebrations", text: "Birthdays measured in scoops and sparklers since your grandparents' day." },
  { icon: Heart, title: "Date Nights", text: "Two spoons, one sundae, and the sea for company." },
  { icon: Umbrella, title: "Seaside Visits", text: "The full Broadstairs day out — beach, bandstand, and a Morelli's cone." },
  { icon: Camera, title: "Tourist Pilgrimages", text: "Visitors travel from across the world to step inside our 1932 time capsule." },
];

const TESTIMONIALS = [
  { quote: "If you are in the area then go! It's like going back in time! Ice cream is excellent. The sundaes are huge and you could easily share one!", name: "Helen G. · Google Review", rating: 4 },
  { quote: "Morelli's ice cream is legendary. We visit Morelli's every time we are at Broadstairs.", name: "Adeline · Google Review", rating: 4 },
  { quote: "Fantastic homemade ice cream overlooking the seafront. Worth paying the price for.", name: "Chris L. · Google Review", rating: 5 },
];

const IMAGES = [
  { src: "/experience-love.webp", alt: "A sharing sundae for two in the Love glass at Morelli's" },
  { src: "/experience-vintage-exterior.webp", alt: "The original Morelli's parlour frontage with its giant ice cream cone" },
  { src: "/experience-singer.webp", alt: "A singer performing for guests on a parlour music night" },
  { src: "/experience-gifts.webp", alt: "Festive gift boxes and sweet cones on the parlour counter" },
];

const Experience = () => (
  <section id="experience" data-testid="experience-section" className="py-28 md:py-36 relative overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <SectionHeading chapter="04" overline="The Experience" title="More Than Ice Cream" align="center" />

      <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {MOMENTS.map((m, i) => (
          <Reveal key={m.title} delay={i * 0.08}>
            <div data-testid={`moment-card-${i}`} className="group h-full bg-mint/50 rounded-[2rem] p-8 border border-transparent hover:border-gold/50 hover:bg-mint/80 transition-colors duration-500">
              <span className="w-12 h-12 rounded-full bg-teal text-cream grid place-items-center group-hover:bg-gold transition-colors duration-300">
                <m.icon size={20} strokeWidth={1.8} />
              </span>
              <h3 className="font-display font-bold text-xl mt-5">{m.title}</h3>
              <p className="text-sm text-teal/70 mt-2 leading-relaxed">{m.text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5 grid grid-cols-2 gap-4">
          {IMAGES.map((img, i) => (
            <Reveal key={img.src} delay={i * 0.07} className={i % 2 === 1 ? "mt-8" : ""}>
              <div className={`overflow-hidden shadow-lg shadow-teal/15 ${i % 2 === 0 ? "rounded-t-full rounded-b-3xl" : "rounded-3xl"} aspect-[3/4]`}>
                <img src={img.src} alt={img.alt} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            </Reveal>
          ))}
        </div>
        <div className="lg:col-span-7 flex flex-col gap-6">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.1}>
              <figure data-testid={`testimonial-${i}`} className="border-l-2 border-gold pl-6 py-2">
                <div className="flex gap-1 text-gold mb-3" aria-label={`${t.rating} out of 5 stars`}>
                  {Array.from({ length: 5 }).map((_, s) => <Star key={s} size={14} fill={s < t.rating ? "currentColor" : "none"} strokeWidth={1.5} />)}
                </div>
                <blockquote className="font-display italic text-xl md:text-2xl leading-snug text-teal">&ldquo;{t.quote}&rdquo;</blockquote>
                <figcaption className="text-xs uppercase tracking-[0.25em] text-teal/60 mt-3">{t.name}</figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default Experience;
